import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Checkbox,
  Button,
  FormControlLabel,
  IconButton,
  Box,
  Select,
  MenuItem,
  CircularProgress,
  TextField,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import CloseIcon from '@material-ui/icons/Close';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import DeleteIcon from '@material-ui/icons/Delete';
import LinkIcon from '@material-ui/icons/Link';
import FormatQuoteIcon from '@material-ui/icons/FormatQuote';
import usePremiumBacklink from '../hooks/usePremiumBacklink';

const OrderModal = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const {
    modalOpen,
    closeModal,
    cart,
    updateCartItemQuantity,
    removeFromCart,
    confirmOrder,
    isCreatingOrder,
    orderError,
    openTermsModal,
    requiresLinkDetails,
  } = usePremiumBacklink();

  const [checked, setChecked] = useState(false);
  // Per-unit link details: { [itemId]: Array<{target_url, desired_anchor}> }
  // Length always equals the cart item's current quantity.
  const [linkDetails, setLinkDetails] = useState({});

  // Reset state when modal closes
  React.useEffect(() => {
    if (!modalOpen) {
      setChecked(false);
      setLinkDetails({});
    }
  }, [modalOpen]);

  // Keep the linkDetails arrays in sync with each item's quantity.
  React.useEffect(() => {
    if (!requiresLinkDetails) return;
    setLinkDetails((prev) => {
      const next = {};
      cart.forEach((item) => {
        const existing = prev[item.id] || [];
        const arr = [];
        for (let i = 0; i < item.quantity; i += 1) {
          arr.push(existing[i] || { target_url: '', desired_anchor: '' });
        }
        next[item.id] = arr;
      });
      return next;
    });
  }, [cart, requiresLinkDetails]);

  const updateLinkDetail = (itemId, index, field, value) => {
    setLinkDetails((prev) => {
      const arr = [...(prev[itemId] || [])];
      arr[index] = { ...(arr[index] || {}), [field]: value };
      return { ...prev, [itemId]: arr };
    });
  };

  const linkDetailsComplete = !requiresLinkDetails || cart.every((item) => {
    const arr = linkDetails[item.id] || [];
    if (arr.length !== item.quantity) return false;
    return arr.every((d) => (d?.target_url || '').trim() && (d?.desired_anchor || '').trim());
  });

  const handleConfirm = () => confirmOrder(linkDetails);

  // Handle quantity change for a cart item
  const handleQtyChange = (itemKey, newQty) => {
    if (updateCartItemQuantity) {
      updateCartItemQuantity(itemKey, newQty);
    }
  };



  // Calculate subtotal and total (add tax/fee if needed)
  const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  // Example: add 3.5% fee
  const fee = subtotal * 0;
  const totalDue = subtotal + fee;

  return (
    <Dialog
      open={modalOpen}
      onClose={closeModal}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ style: { maxHeight: 'calc(100vh - 32px)' } }}
    >
      <DialogTitle disableTypography style={{ 
        display: 'flex', 
        alignItems: 'center', 
        borderBottom: '1px solid #e0e0e0', 
        paddingBottom: 8,
        padding: isMobile ? '12px 16px' : '16px 24px'
      }}>
        <Box display="flex" alignItems="center" mr={2}>
          <ShoppingCartIcon style={{ fontSize: isMobile ? 24 : 32, marginRight: 8 }} />
          <Typography variant={isMobile ? "subtitle1" : "h6"}>Your Cart</Typography>
        </Box>
        <IconButton onClick={closeModal} size="small" style={{ marginLeft: 'auto' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        style={{
          padding: isMobile ? '16px 12px' : '20px 24px',
          overflowY: 'auto',
        }}
      >
        {/* Cart Items */}
        <Box mb={3}>
          {cart.map((item) => (
            <Box
              key={item.id}
              mb={2}
              style={{
                background: '#FFFFFF',
                border: '1px solid #E4E7EB',
                borderRadius: 12,
                overflow: 'hidden',
              }}
            >
              <Box p={2} display="flex" alignItems="center" justifyContent="space-between">
                <Box flex={1} minWidth={0}>
                  <Typography style={{ fontSize: '1rem', fontWeight: 600, color: '#1A2332', lineHeight: 1.3 }}>
                    {item.name}
                  </Typography>
                  <Typography style={{ fontSize: '0.8rem', color: '#6C7480', marginTop: 2 }}>
                    ${item.price.toLocaleString()} each
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap="12px" ml={2}>
                  <Select
                    value={item.quantity}
                    onChange={(e) => handleQtyChange(item.id, e.target.value)}
                    variant="outlined"
                    style={{
                      minWidth: 80,
                      fontSize: '0.85rem',
                      background: '#F5F9FC',
                    }}
                    inputProps={{ style: { padding: '8px 12px' } }}
                  >
                    {[...Array(20).keys()].map((i) => (
                      <MenuItem key={i + 1} value={i + 1}>Qty {i + 1}</MenuItem>
                    ))}
                  </Select>
                  <Typography style={{ fontSize: '1rem', fontWeight: 700, color: '#1A2332', minWidth: 80, textAlign: 'right' }}>
                    ${(item.price * item.quantity).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </Typography>
                  <IconButton
                    onClick={() => removeFromCart(item.id)}
                    size="small"
                    style={{ color: '#8A94A6' }}
                    aria-label="Remove item"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              {requiresLinkDetails && (
                <Box
                  px={2}
                  pt={2}
                  pb={2}
                  style={{
                    background: '#F8FBFD',
                    borderTop: '1px solid #E4E7EB',
                  }}
                >
                  <Box display="flex" alignItems="center" mb={1.5}>
                    <Typography
                      style={{
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        color: '#0E7BAF',
                        textTransform: 'uppercase',
                        letterSpacing: '0.6px',
                      }}
                    >
                      Backlink Details
                    </Typography>
                    <Typography style={{ fontSize: '0.75rem', color: '#6C7480', marginLeft: 8 }}>
                      Required — one target & anchor per backlink
                    </Typography>
                  </Box>
                  {Array.from({ length: item.quantity }).map((_, idx) => {
                    const detail = (linkDetails[item.id] || [])[idx] || {};
                    return (
                      <Box
                        // eslint-disable-next-line react/no-array-index-key
                        key={`${item.id}-${idx}`}
                        mb={idx < item.quantity - 1 ? 1.5 : 0}
                        pt={idx > 0 ? 1.5 : 0}
                        style={idx > 0 ? { borderTop: '1px dashed #DDE5EE' } : {}}
                      >
                        {item.quantity > 1 && (
                          <Typography
                            style={{
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              color: '#6C7480',
                              marginBottom: 6,
                              textTransform: 'uppercase',
                              letterSpacing: '0.4px',
                            }}
                          >
                            Backlink #{idx + 1}
                          </Typography>
                        )}
                        <Box display="grid" gridGap="12px" style={{ gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr' }}>
                          <TextField
                            label="Target URL"
                            placeholder="example.com/page"
                            variant="outlined"
                            size="small"
                            fullWidth
                            required
                            disabled={isCreatingOrder}
                            value={detail.target_url || ''}
                            onChange={(e) => updateLinkDetail(item.id, idx, 'target_url', e.target.value)}
                            helperText="The page you want the backlink to point to"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <LinkIcon style={{ fontSize: 18, color: '#8A94A6' }} />
                                </InputAdornment>
                              ),
                              style: { background: '#FFFFFF' },
                            }}
                          />
                          <TextField
                            label="Desired Anchor"
                            placeholder="best personal injury lawyer"
                            variant="outlined"
                            size="small"
                            fullWidth
                            required
                            disabled={isCreatingOrder}
                            value={detail.desired_anchor || ''}
                            onChange={(e) => updateLinkDetail(item.id, idx, 'desired_anchor', e.target.value)}
                            helperText="The anchor text the backlink should use"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <FormatQuoteIcon style={{ fontSize: 18, color: '#8A94A6' }} />
                                </InputAdornment>
                              ),
                              style: { background: '#FFFFFF' },
                            }}
                          />
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          ))}

          {/* Subtotal and Total */}
          <Box
            mt={1}
            p={2}
            style={{
              background: '#F5F9FC',
              border: '1px solid #DDE5EE',
              borderRadius: 10,
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography style={{ fontSize: '0.85rem', color: '#6C7480' }}>Subtotal</Typography>
              <Typography style={{ fontSize: '0.85rem', color: '#1A2332', fontWeight: 500 }}>
                ${subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </Typography>
            </Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              pt={1}
              style={{ borderTop: '1px solid #DDE5EE' }}
            >
              <Typography style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1A2332' }}>Total due</Typography>
              <Typography style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1A2332' }}>
                ${totalDue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </Typography>
            </Box>
          </Box>
        </Box>
        {/* Terms Section */}
        <Box mb={2}>
          <Typography variant="h6" gutterBottom style={{ fontWeight: 600, color: '#333' }}>
            Order Summary
          </Typography>
          <Typography gutterBottom style={{ color: '#666', lineHeight: 1.6 }}>
            Thank you for your order totaling <strong style={{ color: '#1976d2' }}>${subtotal.toLocaleString()}</strong>. 
            Please review and agree to the terms below before we proceed.
          </Typography>
        </Box>
        
        <Box bgcolor="#f8f9fa" borderRadius={8} p={3} mb={3} border={1} borderColor="#e9ecef">
          <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600, color: '#495057', marginBottom: 16 }}>
            Terms & Conditions
          </Typography>
          <Box component="ul" style={{ margin: 0, paddingLeft: 20, color: '#6c757d' }}>
            <Box component="li" mb={1} style={{ lineHeight: 1.6 }}>
              You are purchasing <strong>premium backlinks</strong> as outlined in your order. 
              Total amount: <strong style={{ color: '#1976d2' }}>${subtotal.toLocaleString()}</strong>
            </Box>
            <Box component="li" mb={1} style={{ lineHeight: 1.6 }}>
              <strong>No payment</strong> will be processed immediately
            </Box>
            <Box component="li" mb={1} style={{ lineHeight: 1.6 }}>
              Your payment method will only be charged <strong>after the link is live and verified</strong>
            </Box>
            <Box component="li" mb={1} style={{ lineHeight: 1.6 }}>
              Backlink placement may take <strong>2–4 weeks depending on your publication</strong>
            </Box>
            <Box component="li" mb={1} style={{ lineHeight: 1.6 }}>
              You will receive confirmation once the link is live and billing is completed
            </Box>
            <Box component="li" style={{ lineHeight: 1.6 }}>
              Please review the complete{' '}
              <button 
                onClick={openTermsModal}
                type="button"
                style={{ 
                  color: '#1976d2', 
                  textDecoration: 'none', 
                  fontWeight: 500,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  fontFamily: 'inherit',
                  fontSize: 'inherit'
                }}
              >
                Terms & Conditions
              </button>
            </Box>
          </Box>
        </Box>
        
        <Box bgcolor="#e3f2fd" borderRadius={8} p={2} mb={2}>
          <FormControlLabel
            control={
              <Checkbox 
                checked={checked} 
                onChange={e => setChecked(e.target.checked)} 
                color="primary"
                style={{ padding: 8 }}
                disabled={isCreatingOrder}
              />
            }
            label={
              <button
                onClick={openTermsModal}
                type="button"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  fontFamily: 'inherit',
                  fontSize: 'inherit',
                  fontWeight: 500,
                  color: '#1976d2',
                  textAlign: 'left',
                  lineHeight: 1.4
                }}
              >
                I agree to the above billing terms and authorize payment once the link is live
              </button>
            }
          />
        </Box>
        
        {/* Error Alert */}
        {orderError && (
          <Box mb={2}>
            <Alert severity="error" variant="outlined">
              {orderError}
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions style={{ 
        padding: isMobile ? '12px 16px' : '16px 24px', 
        borderTop: '1px solid #e0e0e0', 
        backgroundColor: '#fafafa',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '8px' : '0',
        position: 'sticky',
        bottom: 0,
        zIndex: 1
      }}>
        <Button
          onClick={closeModal}
          color="default"
          variant="outlined"
          disabled={isCreatingOrder}
          style={{ 
            marginRight: isMobile ? 0 : 12,
            minWidth: isMobile ? '100%' : 100,
            textTransform: 'none',
            fontWeight: 500
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color="primary"
          variant="contained"
          disabled={!checked || cart.length === 0 || isCreatingOrder || !linkDetailsComplete}
          style={{
            minWidth: isMobile ? '100%' : 140,
            textTransform: 'none',
            fontWeight: 600,
            color: '#fff',
            backgroundColor: (!checked || cart.length === 0 || isCreatingOrder || !linkDetailsComplete) ? '#ccc' : '#4caf50',
            boxShadow: (!checked || cart.length === 0 || isCreatingOrder || !linkDetailsComplete) ? 'none' : '0 2px 8px rgba(76, 175, 80, 0.3)',
            '&:hover': {
              backgroundColor: (!checked || cart.length === 0 || isCreatingOrder || !linkDetailsComplete) ? '#ccc' : '#45a049'
            }
          }}
        >
          {isCreatingOrder && (
            <>
              <CircularProgress size={16} color="inherit" style={{ marginRight: 8 }} />
              {isMobile ? 'Creating...' : 'Creating Order...'}
            </>
          )}
          {!isCreatingOrder && (isMobile ? 'Confirm' : 'Confirm Order')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderModal; 